import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Layers, BarChart3, Bell, Radio, Video, Presentation } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";

const TYPE_META = {
  recorded: { label: "Recorded", icon: Video, cls: "bg-navy text-white" },
  live: { label: "Live Class", icon: Radio, cls: "bg-cherry text-white" },
  workshop: { label: "Workshop", icon: Presentation, cls: "bg-cherry text-white" },
  coming: { label: "Coming Soon", icon: Clock, cls: "bg-sky text-navy" },
};

export default function Courses({ courses, content, onEnquire }) {
  const [filter, setFilter] = useState("All Courses");

  const categories = useMemo(
    () => ["All Courses", ...new Set(courses.map((c) => c.category))],
    [courses]
  );
  const visible = useMemo(
    () => (filter === "All Courses" ? courses : courses.filter((c) => c.category === filter)),
    [courses, filter]
  );

  const priceCurrent = content?.workshop_price_current || "₹499";
  const priceFuture = content?.workshop_price_future || "₹699";
  const priceLabel = content?.workshop_price_label || "First Batch Price";

  return (
    <section id="courses" data-testid="courses-section" className="bg-chalk py-20 sm:py-28 lg:py-36">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div className="max-w-xl">
            <Overline>Course Catalog</Overline>
            <h2 className="mt-6 font-serif font-bold tracking-tight text-navy text-3xl sm:text-4xl lg:text-5xl leading-[1.1]" data-testid="courses-title">
              Explore our courses
            </h2>
            <p className="mt-4 text-base font-sans text-[#5B6B7C] leading-relaxed">
              Recorded video courses you can study anytime, plus live classes and workshops you can register for.
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="course-filter-tabs">
            {categories.map((cat) => (
              <button
                key={cat}
                data-testid={`course-filter-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setFilter(cat)}
                className={`text-xs sm:text-sm font-medium px-4 py-2 rounded-full border transition-all duration-300 ${
                  filter === cat
                    ? "bg-navy text-white border-navy"
                    : "bg-white text-navy/70 border-navy/15 hover:border-navy/40"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          <AnimatePresence mode="popLayout">
            {visible.map((course) => {
              const meta = TYPE_META[course.type] || TYPE_META.recorded;
              const isWorkshop = course.type === "workshop";
              return (
                <motion.article
                  layout
                  key={course.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  data-testid={`course-card-${course.id}`}
                  className="group flex flex-col bg-white rounded-2xl overflow-hidden border border-navy/10 hover:border-cherry/30 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="relative overflow-hidden aspect-[16/10]">
                    <img
                      src={course.thumbnail}
                      alt={course.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/50 to-transparent opacity-60" />
                    <span className={`absolute top-4 left-4 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest px-3 py-1.5 rounded-full ${meta.cls}`}>
                      <meta.icon className="h-3 w-3" />
                      {meta.label}
                    </span>
                  </div>

                  <div className="flex flex-col flex-1 p-6 sm:p-7">
                    <span className="text-[11px] font-mono uppercase tracking-[0.25em] text-cherry">{course.category}</span>
                    <h3 className="mt-3 font-serif text-xl sm:text-2xl font-semibold text-navy leading-snug">{course.title}</h3>
                    <p className="mt-3 text-sm font-sans text-[#5B6B7C] leading-relaxed line-clamp-3">{course.description}</p>

                    <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs font-sans text-navy/60">
                      {course.duration && (
                        <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-cherry" />{course.duration}</span>
                      )}
                      {course.modules && (
                        <span className="inline-flex items-center gap-1.5"><Layers className="h-3.5 w-3.5 text-cherry" />{course.modules}</span>
                      )}
                      {course.level && (
                        <span className="inline-flex items-center gap-1.5"><BarChart3 className="h-3.5 w-3.5 text-cherry" />{course.level}</span>
                      )}
                    </div>

                    <div className="mt-auto pt-6 flex items-center justify-between gap-3 border-t border-navy/10 mt-6">
                      {isWorkshop ? (
                        <div data-testid={`course-price-${course.id}`}>
                          <span className="font-serif text-2xl font-bold text-cherry">{priceCurrent}</span>
                          <span className="ml-2 text-sm font-sans text-navy/40 line-through">{priceFuture}</span>
                          <span className="block text-[10px] font-mono uppercase tracking-widest text-navy/50 mt-0.5">{priceLabel}</span>
                        </div>
                      ) : (
                        <span className="text-sm font-medium text-navy" data-testid={`course-price-${course.id}`}>{course.price_note}</span>
                      )}
                      <button
                        data-testid={`course-enquire-button-${course.id}`}
                        onClick={() => onEnquire(course)}
                        className="inline-flex items-center gap-2 text-sm font-medium bg-navy hover:bg-cherry text-white px-4 py-2.5 rounded-full transition-colors duration-300"
                      >
                        <Bell className="h-3.5 w-3.5" />
                        Enquire
                      </button>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </AnimatePresence>
        </motion.div>

        {visible.length === 0 && (
          <p className="mt-14 text-center text-sm font-sans text-[#5B6B7C]" data-testid="courses-empty-state">
            No courses in this category yet — new modules are added regularly.
          </p>
        )}
      </div>
    </section>
  );
}
